// /var/www/afra/192.testproject.work/maracar8/0.8.2/myapp/src/pages/Sidebar.js

import React, { useState, useEffect } from 'react';
import { fetchCRMSystemInfo, getSelectedLanguage } from '../api/api';
import { languageManager } from '../utils/loadLanguage';
import menuItemsData from '../data/menu/MenuItems.json';
import permissionsData from '../data/menu/PermissionsMenu.json';
import '../css/Sidebar.css';

const Sidebar = () => {
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [openDropdown, setOpenDropdown] = useState(null);
  const [userProfile, setUserProfile] = useState({
    name: '',
    role: '',
    image: ''
  });
  const [language, setLanguage] = useState(getSelectedLanguage());
  const [finalMenuItems, setFinalMenuItems] = useState([]);

  useEffect(() => {
    (async () => {
      await languageManager.loadLanguage(language);
    })();
  }, [language]);

  useEffect(() => {
    setLanguage(getSelectedLanguage());
  }, [getSelectedLanguage()]);

  useEffect(() => {
    const getUserProfile = async () => {
      try {
        const crmSystemInfo = await fetchCRMSystemInfo();
        setUserProfile({
          name: crmSystemInfo['company-name'],
          role: crmSystemInfo['name-system'],
          image: crmSystemInfo['img-profile']
        });
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };
    getUserProfile();
  }, []);

  const decodeUTF8 = (value) => {
    try {
      return decodeURIComponent(atob(value || ''));
    } catch (error) {
      console.error('Error decoding UTF-8:', error);
      return 'N/A';
    }
  };

  const maracarData = JSON.parse(localStorage.getItem('maracar') || '[]')[0] || {};
  const userImage = maracarData.image || userProfile.image;
  const userID = maracarData.user_ID ? decodeUTF8(maracarData.user_ID) : 'N/A';
  const username = maracarData.username ? decodeUTF8(maracarData.username) : 'N/A';
  const position = maracarData.position ? decodeUTF8(maracarData.position) : '';

  const { baseMenu, contentMenu, TourMenu } = menuItemsData;
  const allMenus = [...baseMenu, contentMenu, TourMenu];
  const { specialUserIDs, roles, defaultPermissions } = permissionsData;

  const findMenuByIndex = (menus, targetIndexes) => {
    const menuMap = new Map();
    menus.forEach((menu) => {
      if (targetIndexes.includes(menu.index)) {
        menuMap.set(menu.index, { ...menu, subItems: menu.subItems ? [...menu.subItems] : undefined });
      }
      if (menu.subItems) {
        const matchedSubItems = menu.subItems.filter((sub) => targetIndexes.includes(sub.index));
        if (matchedSubItems.length > 0) {
          if (menuMap.has(menu.index)) {
            menuMap.get(menu.index).subItems = matchedSubItems;
          } else {
            menuMap.set(menu.index, { ...menu, subItems: matchedSubItems });
          }
        }
      }
    });
    return [...menuMap.values()];
  };

  useEffect(() => {
    let allowedIndexes = [];
    const allValidIndexes = allMenus.flatMap(menu => [
      menu.index,
      ...(menu.subItems ? menu.subItems.map(sub => sub.index) : [])
    ]);

    if (specialUserIDs[userID]) {
      allowedIndexes = specialUserIDs[userID].filter(idx => allValidIndexes.includes(idx));
    } else if (roles[position]) {
      allowedIndexes = roles[position].filter(idx => allValidIndexes.includes(idx));
    } else {
      allowedIndexes = defaultPermissions.filter(idx => allValidIndexes.includes(idx));
    }

    //console.log('UserID:', userID, 'Position:', position, 'Allowed Indexes:', allowedIndexes);

    const sortedMenuItems = findMenuByIndex(allMenus, allowedIndexes).sort((a, b) => a.index - b.index);
    sortedMenuItems.forEach((menu) => {
      if (menu.subItems) menu.subItems.sort((a, b) => a.index - b.index);
    });

    //console.log('Final Menu Items:', sortedMenuItems);
    setFinalMenuItems(sortedMenuItems);
  }, [userID, position]);

  const getLabel = (key) => {
    return languageManager.translations?.menu?.[key] || key;
  };

  const handleMenuClick = (menuKey) => {
    setActiveMenu(menuKey);
    setOpenDropdown(null);
  };

  const toggleDropdown = (itemKey) => {
    if (openDropdown === itemKey) {
      setOpenDropdown(null);
    } else {
      setOpenDropdown(itemKey);
    }
  };

  const renderSubItems = (subItems) => {
    return (
      <ul className="submenu">
        {subItems.map((subItem) => (
          <li key={subItem.key} className="submenu-item">
            <a
              href={subItem.path}
              className="menu-item"
              onClick={() => handleMenuClick(subItem.key)}
            >
              <i className={`fas ${subItem.icon}`}></i> {getLabel(subItem.key)}
            </a>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <aside className="sidebar">
      <div className="user-profile">
        <img src={userImage} alt={username} className="user-image" />
        <div className="user-info text-center">
          <h5 className="user-name">ID: {userID} | {username}</h5>
          <p className="user-name">{userProfile.name}</p>
        </div>
      </div>

      <nav className="menu">
        {finalMenuItems.map((item) => (
          <div key={item.key}>
            <a
              href={item.path}
              className={`menu-item ${activeMenu === item.key ? 'active' : ''} ${item.customClass || ''}`}
              onClick={() => (item.subItems ? toggleDropdown(item.key) : handleMenuClick(item.key))}
            >
              <i className={`fas ${item.icon}`}></i> {getLabel(item.key)}
              {item.subItems && <i className="fas fa-chevron-down dropdown-icon"></i>}
            </a>
            {item.subItems && openDropdown === item.key && renderSubItems(item.subItems)}
          </div>
        ))}
      </nav>

      <footer className="sidebar-footer">
        <p>© {new Date().getFullYear()} {userProfile.role}</p>
        <p>{userProfile.name}</p>
      </footer>
    </aside>
  );
};

export default Sidebar;